import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { selectCarouselCards } from '../../../../store/showcase/showcase.selector';
import {
    setIsViewingNewSelection,
    setNewSelectionCardData,
    setPreselectedCardId,
    setPreselectedCardType,
} from '../../../../store/showcase/showcase.action';
import { carouselCaption, coverflowLayout, coverflowSlots } from '../../../../utils/showcase/showcaseCards';
import { useSystemInitialization } from '../../../../hooks/useSystemInitialization';

import {
    AttributeRow,
    CaptionSlide,
    CaptionViewport,
    CarouselCard,
    CarouselEmpty,
    CarouselStage,
    PlayerName,
    PositionRail,
    PositionThumb,
    PositionTrack,
    ShowcaseCarouselContainer,
    TeamName,
    ViewLabel,
    ViewOverlay,
} from './ShowcaseCarousel.styles';
import CardAttribute from '../../../../components/card-attribute/CardAttribute';
import RevealImage, { preloadImages } from '../../../../components/reveal-image/RevealImage';
import PrimaryBtn from '../../../../components/btns/primary-btn/PrimaryBtn';
import LoadingIcon from '../../../../components/loading-icon/LoadingIcon';

const NARROW_QUERY = '(max-width: 768px)';
const CARD_GAP = 28;
const CAROUSEL_SCALE = 1.25;
const SCROLL_MS_PER_CARD = 4200;
const SEEK_MS = 640;
const DRAG_THRESHOLD = 8;
const SPIN_DECAY_MS = 520;
const MAX_SPIN = 0.01;
const SPRING = { type: 'spring', stiffness: 260, damping: 28 };
const TRACK = { duration: 0 };
const CAPTION_SHIFT = 56;
const captionVariants = {
    enter: (direction) => ({ opacity: 0, x: direction * CAPTION_SHIFT }),
    center: { opacity: 1, x: 0 },
    exit: (direction) => ({ opacity: 0, x: direction * -CAPTION_SHIFT }),
};

function rememberDirection(directionRef, before, after) {
    const delta = after - before;
    if (delta > 0.0005) directionRef.current = 1;
    else if (delta < -0.0005) directionRef.current = -1;
}

function mod(value, count) {
    if (count <= 0) return 0;
    return ((value % count) + count) % count;
}

function smoothstep(progress) {
    const t = Math.min(1, Math.max(0, progress));
    return t * t * (3 - 2 * t);
}

function layoutItems(cards, index, radius, stageWidth) {
    const slots = coverflowSlots(cards, index, radius);
    const layout = coverflowLayout(slots.map((slot) => slot.offset), {
        gap: CARD_GAP,
        scale: CAROUSEL_SCALE,
        stageWidth,
        orientationForOffset: (offset) => (
            slots.find((slot) => slot.offset === offset)?.card.imageOrientation
        ),
    });
    const byOffset = new Map(layout.map((slot) => [slot.offset, slot]));
    return slots.map((slot) => ({
        ...slot,
        placed: byOffset.get(slot.offset),
    }));
}

function lerp(from, to, progress) {
    return from + (to - from) * progress;
}

function mixPlacement(from, to, progress) {
    return {
        centerX: lerp(from.centerX, to.centerX, progress),
        layoutWidth: lerp(from.layoutWidth, to.layoutWidth, progress),
        layoutHeight: lerp(from.layoutHeight, to.layoutHeight, progress),
        opacity: lerp(from.opacity, to.opacity, progress),
    };
}

function movingBehind(fromX, toX) {
    if (fromX === 0 || toX === 0) return false;
    return fromX * toX < 0;
}

function blendItems(current, upcoming, progress) {
    const upcomingById = new Map(upcoming.map((item) => [item.card.id, item]));
    const currentCenter = current.find((item) => item.offset === 0);
    const nextCenter = currentCenter ? upcomingById.get(currentCenter.card.id) : null;
    let travel = currentCenter && nextCenter
        ? nextCenter.placed.centerX - currentCenter.placed.centerX
        : 0;
    if (!travel) {
        const shared = current.find((item) => upcomingById.has(item.card.id));
        if (shared) {
            travel = upcomingById.get(shared.card.id).placed.centerX - shared.placed.centerX;
        }
    }

    const items = new Map();
    current.forEach((item) => {
        const match = upcomingById.get(item.card.id);
        if (match) {
            const offset = progress < 0.5 ? item.offset : match.offset;
            const placed = mixPlacement(item.placed, match.placed, progress);
            const behind = movingBehind(item.placed.centerX, match.placed.centerX)
                && progress > 0
                && progress < 1;
            if (behind) placed.opacity = 0;
            items.set(item.card.id, {
                key: item.card.id,
                card: item.card,
                index: progress < 0.5 ? item.index : match.index,
                offset,
                isCenter: offset === 0,
                behind,
                placed,
            });
            return;
        }
        const exitX = item.placed.centerX + travel;
        const exitBehind = movingBehind(item.placed.centerX, exitX) && progress > 0 && progress < 1;
        items.set(item.card.id, {
            key: item.card.id,
            card: item.card,
            index: item.index,
            offset: item.offset,
            isCenter: false,
            behind: exitBehind,
            placed: {
                ...item.placed,
                centerX: lerp(item.placed.centerX, exitX, progress),
                opacity: exitBehind ? 0 : item.placed.opacity,
            },
        });
    });

    upcoming.forEach((item) => {
        if (items.has(item.card.id)) return;
        const entryX = item.placed.centerX - travel;
        const entryBehind = movingBehind(entryX, item.placed.centerX) && progress > 0 && progress < 1;
        items.set(item.card.id, {
            key: item.card.id,
            card: item.card,
            index: item.index,
            offset: item.offset,
            isCenter: item.offset === 0 && progress >= 0.5,
            behind: entryBehind,
            placed: {
                ...item.placed,
                centerX: lerp(entryX, item.placed.centerX, progress),
                opacity: entryBehind ? 0 : item.placed.opacity,
            },
        });
    });

    return [...items.values()];
}

function ShowcaseCard({ card, index, offset, placed, isCenter, behind, settle, onSelect, onView, onHoverChange, motionTransition, dragMovedRef }) {
    const [ready, setReady] = useState(settle);
    const [hovered, setHovered] = useState(false);
    const hoveringRef = useRef(false);
    const settleRef = useRef(settle);
    const imageRef = useRef(card.imageURL);
    const markReady = useCallback(() => setReady(true), []);

    useEffect(() => () => {
        if (hoveringRef.current) onHoverChange(false);
    }, [onHoverChange]);

    useEffect(() => {
        setHovered(false);
    }, [isCenter]);

    useEffect(() => {
        if (imageRef.current === card.imageURL) return;
        imageRef.current = card.imageURL;
        setReady(false);
    }, [card.imageURL]);
    const width = placed.layoutWidth;
    const height = placed.layoutHeight;
    const label = carouselCaption(card).name || 'Showcase card';

    return (
        <CarouselCard
            role="button"
            aria-label={isCenter ? `View ${label}` : `Show ${label}`}
            aria-current={isCenter ? 'true' : undefined}
            aria-hidden={ready ? undefined : true}
            tabIndex={ready ? 0 : -1}
            $isCenter={isCenter}
            $hovered={hovered}
            onPointerEnter={() => {
                hoveringRef.current = true;
                onHoverChange(true);
                if (isCenter) setHovered(true);
            }}
            onPointerLeave={() => {
                hoveringRef.current = false;
                onHoverChange(false);
                setHovered(false);
            }}
            initial={false}
            style={{
                zIndex: behind ? 1 : 20 - Math.abs(offset),
                visibility: ready ? 'visible' : 'hidden',
                pointerEvents: behind ? 'none' : undefined,
            }}
            animate={{
                x: placed.centerX - width / 2,
                y: -height / 2,
                width,
                height,
                opacity: placed.opacity,
                rotate: card.imageOrientation === 'horizontal' ? 90 : 0,
            }}
            transition={motionTransition}
            onClick={(event) => {
                event.stopPropagation();
                if (dragMovedRef.current) {
                    dragMovedRef.current = false;
                    return;
                }
                if (!ready) return;
                if (isCenter) onView(card);
                else onSelect(index);
            }}
            onKeyDown={(event) => {
                if (!ready) return;
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                if (isCenter) onView(card);
                else onSelect(index);
            }}
        >
            <RevealImage
                src={card.imageURL}
                alt=""
                draggable={false}
                settle={settleRef.current}
                onReady={markReady}
                style={{ height: '100%', position: 'relative', width: '100%' }}
            />
            {isCenter && (
                <ViewOverlay $upright={card.imageOrientation === 'horizontal'}>
                    <ViewLabel>VIEW</ViewLabel>
                </ViewOverlay>
            )}
        </CarouselCard>
    );
}

function useCoverflowRadius() {
    const [isNarrow, setIsNarrow] = useState(() => window.matchMedia(NARROW_QUERY).matches);

    useEffect(() => {
        const media = window.matchMedia(NARROW_QUERY);
        const onChange = () => setIsNarrow(media.matches);
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, []);

    return isNarrow ? 1 : 3;
}

function CaptionCopy({ card }) {
    const caption = carouselCaption(card);
    return (
        <>
            {caption.name && <PlayerName>{caption.name}</PlayerName>}
            {caption.team && <TeamName>{caption.team}</TeamName>}
            <TeamName>•</TeamName>
            <AttributeRow>
                {caption.series && <CardAttribute text={caption.series} />}
                {caption.category && <CardAttribute text={caption.category} />}
                {caption.cardNumber && <CardAttribute text={`#${caption.cardNumber}`} />}
                {caption.attributes.map((attribute) => (
                    <CardAttribute key={attribute} text={attribute} />
                ))}
                {caption.parallel && (
                    <CardAttribute text={caption.parallel} color={caption.parallelColor} />
                )}
            </AttributeRow>
        </>
    );
}

export default function ShowcaseCarousel() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const outletContext = useOutletContext();
    const cardsResolved = outletContext?.cardsResolved !== false;
    const cards = useSelector(selectCarouselCards);
    const radius = useCoverflowRadius();
    const stageRef = useRef(null);
    const hoverCountRef = useRef(0);
    const scrollRef = useRef(0);
    const seekRef = useRef(null);
    const pausedRef = useRef(false);
    const motionRef = useRef(false);
    const draggingRef = useRef(false);
    const dragMovedRef = useRef(false);
    const velocityRef = useRef(0);
    const strideRef = useRef(240);
    const dragRef = useRef(null);
    const shownIdsRef = useRef(new Set());
    const directionRef = useRef(1);
    const [stageWidth, setStageWidth] = useState(0);
    const [scrollIndex, setScrollIndex] = useState(0);
    const [tracking, setTracking] = useState(false);
    const [grabbing, setGrabbing] = useState(false);

    const handleCardHover = useCallback((hovering) => {
        hoverCountRef.current += hovering ? 1 : -1;
        if (hoverCountRef.current < 0) hoverCountRef.current = 0;
        pausedRef.current = hoverCountRef.current > 0;
    }, []);

    useLayoutEffect(() => {
        const node = stageRef.current;
        if (!node) return undefined;
        const updateWidth = () => setStageWidth(node.clientWidth);
        updateWidth();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(updateWidth);
        observer.observe(node);
        return () => observer.disconnect();
    }, [cards.length]);
    const {
        checklistSportDefault: defaultSport,
        checklistYearDefault: defaultYear,
        checklistManufacturerDefault: defaultManufacturer,
    } = useSystemInitialization();

    useEffect(() => {
        if (cards.length < 2) return undefined;
        if (process.env.NODE_ENV === 'test') return undefined;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
        motionRef.current = true;
        setTracking(true);
        let frame = 0;
        let last = performance.now();
        const tick = (now) => {
            const seek = seekRef.current;
            if (draggingRef.current) {
                last = now;
                frame = requestAnimationFrame(tick);
                return;
            }
            if (seek) {
                const before = scrollRef.current;
                const amount = Math.min(1, (now - seek.start) / SEEK_MS);
                const eased = smoothstep(amount);
                scrollRef.current = seek.from + (seek.to - seek.from) * eased;
                if (amount >= 1) {
                    scrollRef.current = seek.to;
                    seekRef.current = null;
                    velocityRef.current = 0;
                }
                rememberDirection(directionRef, before, scrollRef.current);
                setScrollIndex(scrollRef.current);
            } else {
                const before = scrollRef.current;
                const cruise = pausedRef.current ? 0 : (now - last) / SCROLL_MS_PER_CARD;
                const spin = velocityRef.current * (now - last);
                if (cruise !== 0 || spin !== 0) {
                    scrollRef.current += cruise + spin;
                    rememberDirection(directionRef, before, scrollRef.current);
                    setScrollIndex(scrollRef.current);
                }
                const decay = Math.exp(-(now - last) / SPIN_DECAY_MS);
                velocityRef.current *= decay;
                if (Math.abs(velocityRef.current) < 0.00001) velocityRef.current = 0;
            }
            last = now;
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => {
            motionRef.current = false;
            cancelAnimationFrame(frame);
        };
    }, [cards.length]);

    const safeIndex = cards.length === 0 ? 0 : mod(Math.floor(scrollIndex), cards.length);
    const scrollProgress = scrollIndex - Math.floor(scrollIndex);

    const selectCard = useCallback((index) => {
        const count = cards.length;
        if (count === 0) return;
        const current = scrollRef.current;
        const base = Math.floor(current);
        const safeBase = mod(base, count);
        let delta = index - safeBase;
        if (delta > count / 2) delta -= count;
        if (delta < -count / 2) delta += count;
        const target = base + delta;
        if (!motionRef.current) {
            const next = mod(index, count);
            rememberDirection(directionRef, scrollRef.current, next);
            scrollRef.current = next;
            setScrollIndex(scrollRef.current);
            return;
        }
        rememberDirection(directionRef, current, target);
        velocityRef.current = 0;
        seekRef.current = { from: current, to: target, start: performance.now() };
    }, [cards.length]);

    const endDrag = useCallback((event) => {
        const drag = dragRef.current;
        if (!drag || event.pointerId !== drag.pointerId) return;
        const samples = drag.samples;
        const first = samples[0];
        const lastSample = samples[samples.length - 1];
        if (drag.moved && lastSample && first && lastSample.time - first.time >= 16) {
            const velocity = (lastSample.scroll - first.scroll) / (lastSample.time - first.time);
            velocityRef.current = Math.max(-MAX_SPIN, Math.min(MAX_SPIN, velocity));
        } else {
            velocityRef.current = 0;
        }
        dragMovedRef.current = drag.moved;
        draggingRef.current = false;
        dragRef.current = null;
        setGrabbing(false);
    }, []);

    const onPointerDown = useCallback((event) => {
        if (event.button !== 0) return;
        seekRef.current = null;
        velocityRef.current = 0;
        draggingRef.current = true;
        dragMovedRef.current = false;
        setGrabbing(true);
        dragRef.current = {
            pointerId: event.pointerId,
            originX: event.clientX,
            samples: [{ time: performance.now(), scroll: scrollRef.current }],
            moved: false,
        };
    }, []);

    const onPointerMove = useCallback((event) => {
        const drag = dragRef.current;
        if (!drag || event.pointerId !== drag.pointerId) return;
        const dx = event.clientX - drag.originX;
        if (!drag.moved) {
            if (Math.abs(dx) <= DRAG_THRESHOLD) return;
            drag.moved = true;
            drag.lastX = drag.originX;
        }
        const now = performance.now();
        const stride = strideRef.current || 240;
        const step = -(event.clientX - drag.lastX) / stride;
        drag.lastX = event.clientX;
        const before = scrollRef.current;
        scrollRef.current += step;
        rememberDirection(directionRef, before, scrollRef.current);
        drag.samples.push({ time: now, scroll: scrollRef.current });
        drag.samples = drag.samples.filter((sample) => now - sample.time <= 90);
        if (drag.samples.length === 0) {
            drag.samples.push({ time: now, scroll: scrollRef.current });
        }
        setScrollIndex(scrollRef.current);
    }, []);

    useEffect(() => {
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', endDrag);
        window.addEventListener('pointercancel', endDrag);
        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', endDrag);
            window.removeEventListener('pointercancel', endDrag);
        };
    }, [endDrag, onPointerMove]);

    const openCard = useCallback((card) => {
        dispatch(setIsViewingNewSelection(true));
        dispatch(setNewSelectionCardData(card));
        dispatch(setPreselectedCardId(card.positionId || null));
        dispatch(setPreselectedCardType(
            card.positionId
                ? (String(card.positionId).startsWith('field-') ? 'field' : 'bullpen')
                : null
        ));
        navigate('/selection-view', { state: { showcaseView: 'carousel' } });
    }, [dispatch, navigate]);

    useEffect(() => {
        if (cards.length === 0) return undefined;
        const count = cards.length;
        const urls = [];
        for (let offset = -(radius + 1); offset <= radius + 1; offset += 1) {
            const imageURL = cards[(safeIndex + offset + count) % count]?.imageURL;
            if (imageURL) urls.push(imageURL);
        }
        preloadImages(urls);
        return undefined;
    }, [cards, radius, safeIndex]);

    if (cards.length === 0 && !cardsResolved) {
        return (
            <ShowcaseCarouselContainer>
                <CarouselEmpty role="status" aria-label="Loading showcase">
                    <LoadingIcon />
                </CarouselEmpty>
            </ShowcaseCarouselContainer>
        );
    }

    if (cards.length === 0) {
        return (
            <ShowcaseCarouselContainer>
                <CarouselEmpty>
                    <PlayerName>No cards yet</PlayerName>
                    <PrimaryBtn
                        title="ADD CARD"
                        onClick={() => navigate(`/checklists/${defaultSport}/${defaultYear}/${defaultManufacturer}`)}
                    />
                </CarouselEmpty>
            </ShowcaseCarouselContainer>
        );
    }

    const count = cards.length;
    const focusIndex = scrollProgress < 0.5 ? safeIndex : mod(safeIndex + 1, count);
    const currentItems = layoutItems(cards, safeIndex, radius, stageWidth);
    const centerItem = currentItems.find((item) => item.offset === 0);
    const neighborItem = currentItems.find((item) => item.offset === 1)
        || currentItems.find((item) => item.offset === -1);
    if (centerItem && neighborItem) {
        const stride = Math.abs(neighborItem.placed.centerX - centerItem.placed.centerX);
        if (stride > 1) strideRef.current = stride;
    }
    const items = blendItems(
        currentItems,
        layoutItems(cards, mod(safeIndex + 1, count), radius, stageWidth),
        scrollProgress,
    );
    const motionTransition = tracking ? TRACK : SPRING;
    const settledIds = new Set(shownIdsRef.current);
    items.forEach((item) => shownIdsRef.current.add(item.card.id));
    const marker = count <= 1 ? 0.5 : mod(scrollIndex, count) / count;

    return (
        <ShowcaseCarouselContainer>
            <CarouselStage
                ref={stageRef}
                $grabbing={grabbing}
                onPointerDown={onPointerDown}
            >
                {items.map(({ key, card, index, offset, placed, isCenter, behind }) => (
                    <ShowcaseCard
                        key={key}
                        card={card}
                        index={index}
                        offset={offset}
                        placed={placed}
                        isCenter={isCenter}
                        behind={behind}
                        settle={settledIds.has(card.id)}
                        onSelect={selectCard}
                        onView={openCard}
                        onHoverChange={handleCardHover}
                        motionTransition={motionTransition}
                        dragMovedRef={dragMovedRef}
                    />
                ))}
            </CarouselStage>
            <PositionRail role="img" aria-label={`Card ${focusIndex + 1} of ${count}`}>
                <PositionTrack />
                <PositionThumb style={{ left: `calc(${marker} * (100% - 28px))` }} />
            </PositionRail>
            <CaptionViewport>
                <AnimatePresence initial={false} custom={directionRef.current}>
                    <CaptionSlide
                        key={cards[focusIndex].id}
                        custom={directionRef.current}
                        variants={captionVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                            duration: process.env.NODE_ENV === 'test'
                                || window.matchMedia('(prefers-reduced-motion: reduce)').matches
                                ? 0
                                : 0.75,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                    >
                        <CaptionCopy card={cards[focusIndex]} />
                    </CaptionSlide>
                </AnimatePresence>
            </CaptionViewport>
        </ShowcaseCarouselContainer>
    );
}
